import { cn } from "@/lib/utils";
import { getMissionDescription } from "@/config/levels";
import { FRUIT_3D_IMAGES, USE_3D_FRUITS } from "@/assets/fruitImages";

// Mission progress display during gameplay
export function MissionProgress({ 
  levelConfig, 
  missionProgress, 
  fruitsCollected,
  dualFruitsCollected,
  score,
  className 
}) {
  const missionType = levelConfig?.missionType || 'collect_fruit';
  const targetCount = levelConfig?.targetCount || 0;
  const targetFruit = levelConfig?.targetFruit;
  const dualMission = levelConfig?.dualMission;
  
  // For dual missions, render a different layout
  if (missionType === 'dual_collect' && dualMission) {
    const obj1 = dualMission.objective1;
    const obj2 = dualMission.objective2;
    const fruit1Progress = dualFruitsCollected?.fruit1 || 0;
    const fruit2Progress = dualFruitsCollected?.fruit2 || 0;
    const fruit1Complete = fruit1Progress >= obj1.count;
    const fruit2Complete = fruit2Progress >= obj2.count;
    
    return (
      <div className={cn(
        "bg-black/30 backdrop-blur-sm rounded-xl p-3 border border-white/10",
        className
      )}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/90 text-sm font-medium">Dual Mission</span>
          {fruit1Complete && fruit2Complete && (
            <span className="text-green-400 text-xs font-bold animate-pulse">✓ COMPLETE!</span>
          )}
        </div>
        
        {/* Objective 1 */}
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            {USE_3D_FRUITS && FRUIT_3D_IMAGES[obj1.fruit] ? (
              <img src={FRUIT_3D_IMAGES[obj1.fruit]} alt={obj1.fruit} className="w-6 h-6 object-contain" />
            ) : (
              <span className="text-lg">{obj1.emoji}</span>
            )}
            <span className={cn(
              "text-xs font-medium",
              fruit1Complete ? "text-green-400" : "text-white/80"
            )}>
              {fruit1Progress}/{obj1.count} {fruit1Complete && "✓"}
            </span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full transition-all duration-300",
                fruit1Complete ? "bg-green-500" : "bg-yellow-500"
              )}
              style={{ width: `${Math.min((fruit1Progress / obj1.count) * 100, 100)}%` }}
            />
          </div>
        </div>
        
        {/* Objective 2 */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            {USE_3D_FRUITS && FRUIT_3D_IMAGES[obj2.fruit] ? (
              <img src={FRUIT_3D_IMAGES[obj2.fruit]} alt={obj2.fruit} className="w-6 h-6 object-contain" />
            ) : (
              <span className="text-lg">{obj2.emoji}</span>
            )}
            <span className={cn(
              "text-xs font-medium",
              fruit2Complete ? "text-green-400" : "text-white/80"
            )}>
              {fruit2Progress}/{obj2.count} {fruit2Complete && "✓"}
            </span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full transition-all duration-300",
                fruit2Complete ? "bg-green-500" : "bg-red-500"
              )}
              style={{ width: `${Math.min((fruit2Progress / obj2.count) * 100, 100)}%` }}
            />
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate current progress based on mission type
  const getCurrentProgress = () => {
    switch (missionType) {
      case 'collect_fruit':
        return fruitsCollected;
      case 'create_striped':
        return missionProgress?.stripedCreated || 0;
      case 'create_wrapped':
        return missionProgress?.wrappedCreated || 0;
      case 'create_disco':
        return missionProgress?.discoCreated || 0;
      case 'break_ice':
        return missionProgress?.iceBroken || 0;
      case 'break_chains':
        return missionProgress?.chainsBroken || 0;
      case 'clear_chocolate':
        return missionProgress?.chocolateCleared || 0;
      case 'drop_ingredient':
        return missionProgress?.ingredientsDropped || 0;
      case 'reach_score':
        return score;
      case 'combo_mission':
      case 'special_combo':
        return missionProgress?.specialCombos || 0;
      case 'boss_fight':
        // Boss fight progress is handled separately (by BossFight component)
        // Show 0 progress here - actual HP display is in BossHPBar
        return missionProgress?.bossDefeated ? 1 : 0;
      default:
        return fruitsCollected;
    }
  };
  
  const progress = getCurrentProgress();
  const progressPercent = Math.min((progress / targetCount) * 100, 100);
  const isComplete = progress >= targetCount;
  
  // Get mission icon based on type - use 3D image for fruit collection
  const getMissionIcon = () => {
    switch (missionType) {
      case 'create_striped': return '⚡';
      case 'create_wrapped': return '🎁';
      case 'create_disco': return '🍉';
      case 'break_ice': return '🧊';
      case 'break_chains': return '⛓️';
      case 'clear_chocolate': return '🍫';
      case 'drop_ingredient': return levelConfig?.ingredients?.emoji || '🫒';
      case 'reach_score': return '🏆';
      case 'combo_mission':
      case 'special_combo': return '💥';
      case 'boss_fight': return levelConfig?.boss?.type === 'owl' ? '🦉' : '🐉';
      case 'collect_fruit':
      default: 
        // For fruit collection, return null to use 3D image
        return null;
    }
  };
  
  // Check if we should use 3D image for this mission
  const use3DImage = missionType === 'collect_fruit' && USE_3D_FRUITS && targetFruit && FRUIT_3D_IMAGES[targetFruit];
  const missionIcon = getMissionIcon();
  
  // Get color based on mission type
  const getMissionColor = () => {
    switch (missionType) {
      case 'break_ice': return 'from-cyan-500 to-blue-500';
      case 'break_chains': return 'from-gray-500 to-slate-600';
      case 'clear_chocolate': return 'from-amber-600 to-orange-600';
      case 'drop_ingredient': return 'from-emerald-500 to-teal-500';
      case 'create_striped': return 'from-yellow-400 to-orange-500';
      case 'create_wrapped': return 'from-pink-500 to-rose-500';
      case 'create_disco': return 'from-purple-500 to-pink-500';
      case 'reach_score': return 'from-amber-400 to-yellow-500';
      case 'combo_mission':
      case 'special_combo': return 'from-red-500 to-orange-500';
      case 'boss_fight': return 'from-red-600 to-rose-600';
      default: return 'from-green-500 to-emerald-500';
    }
  };

  return (
    <div className={cn(
      "bg-black/30 backdrop-blur-sm rounded-xl p-3 border border-white/10",
      className
    )}>
      {/* Mission header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {use3DImage ? (
            <img 
              src={FRUIT_3D_IMAGES[targetFruit]} 
              alt={targetFruit}
              className="w-8 h-8 object-contain drop-shadow-md"
            />
          ) : (
            <span className="text-2xl">{missionIcon || levelConfig?.targetEmoji || '🍎'}</span>
          )}
          <span className="text-white/90 text-sm font-medium">Mission</span>
        </div>
        {isComplete && (
          <span className="text-green-400 text-xs font-bold animate-pulse">
            ✓ COMPLETE!
          </span>
        )}
      </div>
      
      {/* Mission description */}
      <p className="text-white/80 text-xs mb-2">
        {getMissionDescription(levelConfig)}
      </p>
      
      {/* Progress bar */}
      <div className="relative">
        <div className="h-4 bg-black/40 rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-500 bg-gradient-to-r",
              getMissionColor(),
              isComplete && "animate-pulse"
            )}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        
        {/* Progress text overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-xs font-bold drop-shadow-lg">
            {progress.toLocaleString()} / {targetCount.toLocaleString()}
          </span>
        </div>
      </div>
      
      {/* Progress percentage */}
      <div className="text-right mt-1">
        <span className={cn(
          "text-xs font-semibold",
          isComplete ? "text-green-400" : "text-white/60"
        )}>
          {Math.round(progressPercent)}%
        </span>
      </div>
    </div>
  );
}

// Compact version for smaller screens
export function MissionProgressCompact({ 
  levelConfig, 
  missionProgress, 
  fruitsCollected,
  score 
}) {
  const missionType = levelConfig?.missionType || 'collect_fruit';
  const targetCount = levelConfig?.targetCount || 0;
  
  const getCurrentProgress = () => {
    switch (missionType) {
      case 'collect_fruit': return fruitsCollected;
      case 'create_striped': return missionProgress?.stripedCreated || 0;
      case 'create_wrapped': return missionProgress?.wrappedCreated || 0;
      case 'create_disco': return missionProgress?.discoCreated || 0;
      case 'break_ice': return missionProgress?.iceBroken || 0;
      case 'break_chains': return missionProgress?.chainsBroken || 0;
      case 'clear_chocolate': return missionProgress?.chocolateCleared || 0;
      case 'drop_ingredient': return missionProgress?.ingredientsDropped || 0;
      case 'reach_score': return score;
      case 'combo_mission':
      case 'special_combo': return missionProgress?.specialCombos || 0;
      case 'boss_fight': return missionProgress?.bossDefeated ? 1 : 0;
      default: return fruitsCollected;
    }
  };
  
  const progress = getCurrentProgress();
  const progressPercent = Math.min((progress / targetCount) * 100, 100);
  
  return (
    <div className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-1.5">
      <span className="text-lg">{levelConfig?.targetEmoji || '🎯'}</span>
      <div className="flex-1 h-2 bg-black/40 rounded-full overflow-hidden min-w-[60px]">
        <div 
          className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <span className="text-white text-xs font-semibold whitespace-nowrap">
        {progress}/{targetCount}
      </span>
    </div>
  );
}
